import React, { useMemo } from 'react'
import { Bar } from 'react-chartjs-2'

function ChartReport(props) {
    const { data: chartData = {}, getQuantityType } = props
    const formatPercent = (input) => {
        const n = Number(input)
        if (Number.isNaN(n)) return ''
        if (Number.isInteger(n)) return `${n}%`
        return n.toFixed(2).replace('.', ',') + '%'
    }

    const { labels: labels1, items = [], total = 0 } = chartData
    const barData = items.map(i => i.value)
    const percents = useMemo(() => items.map(i => (total === 0 ? 0 : Number(((i.value / total) * 100).toFixed(2)))), [items, total])

    const defaultPluginOpts = {
        fontSize: 12,
        fontWeight: '600',
        minHeightForInside: 20,
        insidePadding: 8,
        outsideOffset: 3,
        lineOffset: 8,
        skipZero: false,
        insideColor: '#fff',
        topColor: '#111',
        lineColor: '#111',
        fallbackColor: '#111',
    }

    const data = useMemo(() => ({
        labels: labels1,
        datasets: [
            {
                type: 'bar',
                label: getQuantityType(),
                data: barData,
                backgroundColor: 'rgba(63, 39, 255, 0.3)',
                borderColor: 'rgba(63, 39, 255, 1)',
                borderWidth: 1,
                yAxisID: 'y-axis-2',
            },
            {
                type: 'line',
                label: 'Tỷ lệ %',
                data: percents,
                backgroundColor: 'rgba(255, 99, 132, 0.4)',
                // tension: 0.25,
                lineTension: 0.3,
                pointRadius: 4,
                borderWidth: 2,
                yAxisID: 'y-axis-1',
                fill: false,
                borderColor: 'rgba(255, 99, 132, 1)',
                datalabels: {
                    display: false, // 👈 Ẩn cho Line
                },
            },
        ],
    }), [labels1, items, percents])

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 800,         // thời gian vẽ (ms)
            easing: 'easeOutQuart',  // easing (bạn có thể đổi)
        },
        plugins: {
            drawLabelsPlugin: defaultPluginOpts
            // datalabels: {
            //     display: true,
            //     color: "black",
            //     anchor: "end",
            //     align: "start",
            //     formatter: (value) => `${value}`,
            //     rotation: 90,
            //     font: {
            //         weight: "bold",
            //         size: 12,
            //     },
            // },
        },
        legend: {
            position: 'bottom'
        },
        scales: {
            xAxes: [
                {
                    ticks: { autoSkip: false },
                    gridLines: { display: false }
                },
            ],
            yAxes: [
                {
                    display: false,
                    id: 'y-axis-2',
                    position: 'right',
                    ticks: { beginAtZero: true }
                },
                {
                    id: 'y-axis-1',
                    position: 'left',
                    ticks: {
                        beginAtZero: true,
                        callback: function (value) {
                            return `${value}%`;
                        },
                        max: 100,
                    },
                    gridLines: { drawOnChartArea: true },
                    scaleLabel: { display: true, labelString: '%' },
                    display: true // hide percent axis visually
                }
            ]
        },
        tooltips: {
            mode: 'index',
            intersect: false,
            enabled: true,
        },
    }), [])

    const drawLabelsPlugin = useMemo(() => {
        // helper: rounded rect
        function roundRect(ctx, x, y, w, h, r) {
            if (w < 2 * r) r = w / 2
            if (h < 2 * r) r = h / 2
            ctx.beginPath()
            ctx.moveTo(x + r, y)
            ctx.arcTo(x + w, y, x + w, y + h, r)
            ctx.arcTo(x + w, y + h, x, y + h, r)
            ctx.arcTo(x, y + h, x, y, r)
            ctx.arcTo(x, y, x + w, y, r)
            ctx.closePath()
        }

        return {
            id: 'drawLabelsPlugin',
            afterDatasetsDraw: function (chart, easing) {
                const ctx = chart.ctx
                const opts = (chart.options && chart.options.plugins && chart.options.plugins.drawLabelsPlugin) || {}
                const fontSize = opts.fontSize ?? 12
                const fontWeight = opts.fontWeight ?? '600'
                ctx.font = `${fontWeight} ${fontSize}px sans-serif`
                ctx.textAlign = 'center'
                ctx.lineWidth = 1

                // count visible datasets
                let visibleDatasetsCount = 0
                chart.data.datasets.forEach((ds, di) => {
                    const meta = chart.getDatasetMeta(di)
                    const dsVisible = !(meta && meta.hidden) && !ds.hidden
                    if (dsVisible) visibleDatasetsCount++
                })

                const multiMode = visibleDatasetsCount > 1

                // If multiMode -> draw tooltip boxes at each X index (one per label)
                if (multiMode) {
                    // disable canvas tooltip hover
                    if (chart.options && chart.options.tooltips) chart.options.tooltips.enabled = false

                    const labels = chart.data.labels || []
                    const canvas = chart.canvas;
                    const padding = opts.tooltipBoxPadding ?? 6
                    const radius = opts.tooltipBoxRadius ?? 6
                    const bg = opts.tooltipBoxBgColor ?? 'rgba(0, 0, 0, 0.7)'
                    const border = opts.tooltipBoxBorder ?? '#ddd'
                    const textColor = opts.tooltipTextColor ?? '#fff'
                    const offsetY = opts.outsideOffset ?? 6
                    const lineHeight = Math.round(fontSize * 1.2)

                    // For each index, collect visible elements and build content
                    for (let i = 0; i < labels.length; i++) {
                        const lines = []
                        // first line: label (bold)
                        const labelText = labels[i] != null ? String(labels[i]) : `#${i}`
                        lines.push({ text: labelText, bold: true })

                        // collect dataset entries for this index
                        const entries = []
                        chart.data.datasets.forEach((ds, di) => {
                            const meta = chart.getDatasetMeta(di)
                            if (!meta || meta.hidden || ds.hidden) return
                            const el = Array.isArray(meta.data) ? meta.data[i] : null
                            if (!el || el.hidden) return;
                            // determine display text: percent for line (y-axis is percent), raw for others
                            let display;
                            // try to detect line by ds.type or meta.type
                            const dsType = ds.type || (meta && meta.type) || '';
                            const isLine = dsType === 'line' || String(ds.label).toLowerCase().includes('tỷ') || false;
                            if (isLine) {
                                display = formatPercent(ds.data[i]).replace('%', '')
                            } else {
                                display = String(ds.data[i]);
                            }
                            console.log(1111, ds)
                            // color for dot: try borderColor then backgroundColor
                            const color = (ds.type === 'bar' ? '#c5beff' : '#ff6384');
                            entries.push({ label: ds.label || `Series ${di + 1}`, value: display, color, element: el });
                        })

                        if (entries.length === 0) continue; // nothing to show at this index

                        // build lines for each entry
                        for (const e of entries) {
                            lines.push({ text: `${e.label}: ${e.value}`, color: e.color });
                        }

                        // measure widths
                        let maxW = 0;
                        // use a regular font for measurements
                        const normalFont = `${fontWeight} ${fontSize}px sans-serif`;
                        ctx.font = normalFont;
                        for (const ln of lines) {
                            const w = ctx.measureText(ln.text).width;
                            if (w > maxW) maxW = w;
                        }
                        const boxW = Math.ceil(maxW + padding * 2 + 16); // small extra for dot
                        const boxH = Math.ceil(lines.length * lineHeight + padding * 2);

                        // compute x: average x of elements (or element.x of first entry)
                        let xs = [];
                        for (const e of entries) {
                            const model = e.element._model || {};
                            const elX = (typeof model.x === 'number') ? model.x : (e.element.x || (e.element.tooltipPosition && e.element.tooltipPosition().x) || 0);
                            xs.push(elX);
                        }
                        const x = xs.length ? (xs.reduce((a, b) => a + b, 0) / xs.length) : (chart.chartArea.left + chart.chartArea.width / 2);

                        // compute y: topmost visual (min y) of the elements, then put box above it
                        const ys = entries.map(e => {
                            const model = e.element._model || {};
                            const elY = (typeof model.y === 'number') ? model.y : (e.element.y || (e.element.tooltipPosition && e.element.tooltipPosition().y) || 0);
                            // if bar, use visual top instead of center: try to get model.base
                            if (e.element._model && typeof e.element._model.base === 'number') {
                                return Math.min(elY, e.element._model.base);
                            }
                            return elY;
                        });
                        const topY = Math.min(...ys);
                        // candidate boxY above topY
                        let boxX = x - boxW / 2;
                        let boxY = topY - boxH - offsetY;

                        // keep inside canvas horizontally
                        if (boxX < 4) boxX = 4;
                        if (boxX + boxW > canvas.width - 4) boxX = canvas.width - 4 - boxW;
                        // if not enough space above, place below the max element
                        if (boxY < 4) {
                            const bottomY = Math.max(...ys);
                            boxY = bottomY + offsetY;
                            // if below overflows bottom, clamp
                            if (boxY + boxH > canvas.height - 4) {
                                boxY = Math.max(4, canvas.height - 4 - boxH);
                            }
                        }

                        // draw box
                        ctx.save();
                        // background
                        ctx.fillStyle = bg;
                        ctx.strokeStyle = border;
                        roundRect(ctx, boxX, boxY, boxW, boxH, radius);
                        ctx.fill();
                        ctx.stroke();

                        // draw lines text + colored dot
                        ctx.fillStyle = textColor;
                        ctx.textBaseline = 'top';
                        let ty = boxY + padding;
                        ctx.font = `${fontWeight} ${fontSize}px sans-serif`;
                        console.log(22222, lines)
                        // first line (label) bold-ish
                        const first = lines[0];
                        ctx.fillStyle = textColor;
                        ctx.fillText(first.text, boxX + boxW / 2, ty); // centered
                        ty += lineHeight;

                        // remaining lines
                        ctx.font = normalFont;
                        for (let li = 1; li < lines.length; li++) {
                            const ln = lines[li];
                            // draw dot
                            const dotX = boxX + padding + 6;
                            const dotY = ty + (lineHeight / 2);
                            ctx.beginPath();
                            ctx.fillStyle = ln.color || '#000';
                            ctx.arc(dotX, dotY - 2, 4, 0, Math.PI * 2);
                            ctx.fill();
                            // text
                            ctx.fillStyle = textColor;
                            ctx.textAlign = 'left';
                            ctx.fillText(ln.text, boxX + padding + 16, ty);
                            ty += lineHeight;
                        }

                        ctx.restore();
                    }

                    // don't draw per-element static labels in multiMode
                    return;
                }
                // single dataset: ensure canvas tooltip enabled and remove HTML tooltips
                if (chart.options && chart.options.tooltips) chart.options.tooltips.enabled = true
                // SINGLE DATASET MODE: vẽ labels tĩnh như cũ
                chart.data.datasets.forEach(function (dataset, datasetIndex) {
                    const meta = chart.getDatasetMeta(datasetIndex)

                    // respect visibility (v2)
                    const dsVisible = !(meta && meta.hidden) && !dataset.hidden
                    if (!dsVisible) return

                    // find y scale for this dataset (v2)
                    const scaleId = meta && meta.yAxisID ? meta.yAxisID : (dataset.yAxisID || (chart.options && chart.options.scales && chart.options.scales.yAxes && chart.options.scales.yAxes[0] && chart.options.scales.yAxes[0].id) || 'y-axis-0')
                    const yScale = chart.scales && chart.scales[scaleId] ? chart.scales[scaleId] : (chart.scales && Object.values(chart.scales)[0])

                    meta.data.forEach(function (element, index) {
                        if (!element) return
                        if (element.hidden) return

                        const model = element._model || {}
                        const elX = (typeof model.x === 'number') ? model.x : (element.x || (element.tooltipPosition && element.tooltipPosition().x) || 0)
                        const elY = (typeof model.y === 'number') ? model.y : (element.y || (element.tooltipPosition && element.tooltipPosition().y) || 0)
                        const elBase = (typeof model.base === 'number') ? model.base : (typeof model.y === 'number' && typeof model.height === 'number' ? model.y + model.height : (yScale ? yScale.getPixelForValue(0) : elY))

                        const dataValue = dataset.data[index]
                        const dsType = dataset.type || meta.type || ''
                        const constructorName = element && element.constructor && element.constructor.name ? element.constructor.name.toLowerCase() : ''
                        const isBar = dsType === 'bar' || meta.type === 'bar' || constructorName.includes('rect') || constructorName.includes('bar')
                        const isLine = dsType === 'line' || meta.type === 'line' || constructorName.includes('point') || constructorName.includes('line')

                        ctx.save()

                        if (isBar) {
                            // compute top/base/height
                            const topY = (typeof model.y === 'number') ? model.y : elY
                            const baseY = (typeof model.base === 'number') ? model.base : elBase
                            const visualTop = Math.min(topY, baseY) // pixel visually upper
                            const centerY = (typeof topY === 'number' && typeof baseY === 'number') ? (topY + baseY) / 2 : topY
                            const height = Math.abs((typeof baseY === 'number' ? baseY : topY) - topY)

                            // INSIDE label moved to near-top INSIDE the bar when there's enough height.
                            // If not enough height, place outside above the bar.
                            const minHeightForInside = opts.minHeightForInside ?? (fontSize + 8) // threshold
                            const insidePadding = opts.insidePadding ?? 8 // pixels from visual top
                            let insideY

                            if (height > minHeightForInside) {
                                // place inside near visual top + padding (so label sits inside, slightly below top edge)
                                insideY = visualTop + insidePadding + fontSize / 2
                                // ensure insideY does not go below base (safety)
                                const maxInsideY = Math.max(visualTop + height - fontSize / 2, visualTop)
                                if (insideY > maxInsideY) insideY = centerY
                            } else {
                                // not enough room: put label outside above the bar
                                insideY = visualTop - (opts.outsideOffset ?? 3)
                            }

                            // choose color: white when enough height, otherwise dark
                            const insideColor = '#111'
                            ctx.fillStyle = insideColor
                            ctx.textBaseline = height > minHeightForInside ? 'middle' : 'bottom'
                            ctx.fillText(String(dataValue), elX, insideY)
                        } else if (isLine) {
                            // POINT label (show percent above the point)
                            ctx.textBaseline = 'bottom'
                            ctx.fillStyle = opts.lineColor ?? '#111'
                            const offset = opts.lineOffset ?? 8
                            const px = (typeof model.x === 'number') ? model.x : elX
                            const py = (typeof model.y === 'number') ? model.y : elY
                            ctx.fillText(formatPercent(dataValue), px, py - offset)
                        } else {
                            // fallback
                            ctx.textBaseline = 'bottom'
                            ctx.fillStyle = opts.fallbackColor ?? '#111'
                            ctx.fillText(String(dataValue), elX, elY - 6)
                        }

                        ctx.restore()
                    })
                })
            }
        }
    }, [formatPercent, total])


    return (
        <div className='ChartReport'>
            <div style={{ border: '1px solid #eee', borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                </div>

                <div style={{ width: '100%', height: '600px' }}>
                    <Bar data={data} options={options} plugins={[drawLabelsPlugin]} />
                </div>
            </div>
        </div>
    )
}

export default ChartReport
